import React from 'react';
import { 
    Box, 
    Typography, 
    useTheme, 
    useMediaQuery,
    Stack,
    Paper,
    Divider
} from '@mui/material';
import { 
    Package, 
    Search, 
    MoreVertical, 
    AlertCircle,
    Box as BoxIcon,
    Laptop,
    Headphones
} from 'lucide-react';

const InventoryHeroSection = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Sample data for the mock inventory list
    const inventoryItems = [
        { id: 1, name: 'Pro Wireless Headphones', sku: 'SKU-842', stock: 145, status: 'healthy', icon: <Headphones size={18} /> },
        { id: 2, name: 'Ergonomic Office Chair', sku: 'SKU-901', stock: 4, status: 'low', icon: <BoxIcon size={18} /> },
        { id: 3, name: '16-inch Dev Laptop', sku: 'SKU-112', stock: 32, status: 'healthy', icon: <Laptop size={18} /> },
    ];

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: '100vw',
                overflowX: 'hidden',
                boxSizing: 'border-box',
                backgroundColor: '#F8FAFC',
                py: { xs: 8, md: 12 },
                display: 'flex',
                justifyContent: 'center',
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    maxWidth: '1280px',
                    mx: 'auto',
                    px: { xs: 3, md: 6 },
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row', // Left to Right flow
                    alignItems: 'center',
                    gap: { xs: 8, md: 8 },
                }}
            >
                {/* Left Section: Text & Icon */}
                <Box sx={{ flex: 1, textAlign: isMobile ? 'center' : 'left' }}>
                    
                    <Typography
                        variant="h1"
                        sx={{ 
                            color: '#0F172A',
                            fontSize: { xs: '2.5rem', sm: '3.5rem', lg: '4rem' }, 
                            fontWeight: 800, 
                            lineHeight: 1.1,
                            letterSpacing: '-0.03em',
                            mb: 4
                        }}
                    >
                        Manage <Box component="span" sx={{ color: '#4F46E5' }}>inventory</Box> <br />
                        effortlessly
                    </Typography>

                    <Stack 
                        direction={{ xs: 'column', md: 'row' }} 
                        spacing={3} 
                        alignItems={{ xs: 'center', md: 'flex-start' }}
                    >


                        {/* Description Text */}
                        <Typography 
                            variant="body1" 
                            sx={{ 
                                color: '#475569', 
                                fontSize: '1.125rem',
                                lineHeight: 1.7,
                                maxWidth: '500px',
                            }}
                        >
                            Keep track of your products with our robust inventory management system. Easily add, update, and monitor stock levels in real-time. Get low stock alerts and optimize your inventory to maximize sales.
                        </Typography>
                    </Stack>
                </Box>

                {/* Right Section: Mock UI Data (Replaces Image) */}
                <Box sx={{ 
                    flex: 1, 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    position: 'relative',
                    width: '100%',
                    height: { xs: '450px', md: '550px' }
                }}>
                    
                    {/* Abstract Background Blob (Warm Amber/Orange for Inventory) */}
                    <Box
                        sx={{
                            position: 'absolute',
                            height: { xs: '300px', sm: '400px' },
                            width: { xs: '300px', sm: '400px' },
                            background: 'linear-gradient(135deg, #FEF3C7 0%, #FFEDD5 100%)', // Soft amber to orange
                            borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%', 
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%) rotate(-25deg)',
                            zIndex: 0,
                            opacity: 0.8,
                            filter: 'blur(20px)'
                        }}
                    />

                    {/* Main Mock Inventory Card */}
                    <Paper
                        elevation={0}
                        sx={{
                            position: 'relative',
                            zIndex: 1,
                            width: '100%',
                            maxWidth: '380px',
                            backgroundColor: '#ffffff',
                            borderRadius: '24px',
                            p: 3,
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.1)',
                            border: '1px solid rgba(226, 232, 240, 0.8)',
                        }}
                    >
                        {/* Header */}
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                            <Typography sx={{ fontSize: '1.1rem', color: '#0F172A', fontWeight: 700 }}>
                                Stock Levels
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Box sx={{ p: 1, bgcolor: '#F8FAFC', borderRadius: '8px', cursor: 'pointer' }}>
                                    <Search size={16} color="#64748B" />
                                </Box>
                                <Box sx={{ p: 1, bgcolor: '#F8FAFC', borderRadius: '8px', cursor: 'pointer' }}>
                                    <MoreVertical size={16} color="#64748B" />
                                </Box>
                            </Box>
                        </Stack>

                        <Divider sx={{ mb: 2, borderColor: '#F1F5F9' }} />

                        {/* Inventory List */}
                        <Stack spacing={2.5}>
                            {inventoryItems.map((item) => (
                                <Stack key={item.id} direction="row" alignItems="center" justifyContent="space-between">
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        {/* Item Icon */}
                                        <Box sx={{ 
                                            width: 40, 
                                            height: 40, 
                                            bgcolor: '#F8FAFC', 
                                            borderRadius: '10px', 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center',
                                            color: '#64748B'
                                        }}>
                                            {item.icon}
                                        </Box>
                                        {/* Item Name & SKU */}
                                        <Box>
                                            <Typography sx={{ fontSize: '0.85rem', color: '#0F172A', fontWeight: 600 }}>
                                                {item.name}
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.75rem', color: '#94A3B8' }}>
                                                {item.sku}
                                            </Typography>
                                        </Box>
                                    </Stack>

                                    {/* Stock Status Badge */}
                                    <Box sx={{ textAlign: 'right' }}>
                                        <Typography sx={{ fontSize: '0.9rem', color: '#0F172A', fontWeight: 700 }}>
                                            {item.stock}
                                        </Typography>
                                        <Typography sx={{ 
                                            fontSize: '0.7rem', 
                                            fontWeight: 600,
                                            color: item.status === 'low' ? '#EF4444' : '#10B981',
                                            bgcolor: item.status === 'low' ? '#FEF2F2' : '#ECFDF5',
                                            px: 1,
                                            py: 0.25,
                                            borderRadius: '4px',
                                            mt: 0.5,
                                            display: 'inline-block'
                                        }}>
                                            {item.status === 'low' ? 'Low Stock' : 'In Stock'}
                                        </Typography>
                                    </Box>
                                </Stack>
                            ))}
                        </Stack>
                    </Paper>

                    {/* Floating Alert Card */}
                    <Paper
                        elevation={0}
                        sx={{
                            position: 'absolute',
                            bottom: { xs: '5%', md: '10%' },
                            left: { xs: '0%', sm: '5%', md: '-10%' },
                            zIndex: 2,
                            backgroundColor: '#ffffff',
                            borderRadius: '16px',
                            p: 2,
                            boxShadow: '0 20px 40px -10px rgba(0, 0, 0, 0.15)',
                            border: '1px solid rgba(226, 232, 240, 0.8)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            width: '240px'
                        }}
                    >
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            bgcolor: '#FEF2F2', 
                            color: '#EF4444', 
                            width: 36, 
                            height: 36, 
                            borderRadius: '50%',
                            flexShrink: 0
                        }}>
                            <AlertCircle size={20} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: '0.85rem', color: '#0F172A', fontWeight: 700 }}>Action Required</Typography>
                            <Typography sx={{ fontSize: '0.75rem', color: '#64748B', lineHeight: 1.4, mt: 0.5 }}>
                                Ergonomic Office Chair is running critically low (4 left).
                            </Typography>
                        </Box>
                    </Paper>

                </Box>
            </Box>
        </Box>
    );
};

export default InventoryHeroSection;